import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getPageNumbers } from "../utils";
import { apiFetch } from "../apiFetch";

/* -------------------- HELPERS -------------------- */

const transformAgencyData = (apiData) => {
  const grouped = {};

  apiData.forEach((item) => {
    const name = item.agency_name;
    const composite_key = item.composite_key;
    if (!grouped[composite_key]) {
      grouped[composite_key] = {
        name,
        id: item.composite_key, // keep full key for drill-down
        total: 0,
        matched: 0,
        unmatched: 0,
        onlySource: 0,
        onlyTarget: 0,
        resolved:0,
        ignored:0
      };
    }

    grouped[composite_key].total += Number(item.total_transactions);
    grouped[composite_key].matched += Number(item.matched_count);
    grouped[composite_key].unmatched += Number(item.unmatched_count);
    grouped[composite_key].onlySource += Number(item.only_source_count);
    grouped[composite_key].onlyTarget += Number(item.only_target_count);
    grouped[composite_key].resolved += Number(item.resolved_count);
    grouped[composite_key].ignored += Number(item.ignored_count);


  });

  return Object.values(grouped).map((agency) => {
    const progress =
      agency.total === 0
        ? 0
        : (((agency.matched + agency.ignored + agency.resolved) / agency.total) * 100).toFixed(1);

    let status = "Completed";
    if (agency.unmatched > 0) {
      status = "Action Required";
    } else if (progress < 100) {
      status = "In Progress";
    }

    return {
      ...agency,
      progress: Number(progress),
      status,
    };
  });
};

const getStatusStyle = (status) => {
  switch (status) {
    case "Completed":
      return "bg-green-100 text-green-700";
    case "Action Required":
      return "bg-red-100 text-red-700";
    case "In Progress":
      return "bg-yellow-100 text-yellow-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};


/* -------------------- UI COMPONENTS -------------------- */


const SummaryCard = ({ title, value, subtitle }) => (
  <div className="bg-white p-6 rounded-xl shadow-sm">
    <p className="text-xs text-gray-500 uppercase">{title}</p>
    <p className="text-3xl font-bold">{value}</p>
    <p className="text-sm text-gray-500">{subtitle}</p>
  </div>
);

const AgencyRow = ({ agency, navigate,state }) => (
  <div className="bg-white p-4 rounded-xl shadow-sm grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
    <div className="lg:col-span-2">
      <p
        className="font-bold cursor-pointer hover:text-blue-600"
        onClick={() => navigate(`/programs/config/run/agency/details/${agency.id}`,{
                    state: {file_mapping_id:state.file_mapping_id,uploadedFileId:state.id,agency:agency,source_file_name:state.source_file_name,target_file_name:state.target_file_name,config_name:state.config_name},
        })}
 
      >
        {agency.name}
      </p>
      <p className="text-xs text-gray-500 truncate">{agency.id}</p>
    </div>

    <div className="lg:col-span-2 text-center font-bold">
      {agency.total.toLocaleString()}
    </div>

    <div className="lg:col-span-4 flex justify-center gap-4 text-sm">
      <div>
        <p className="text-gray-500">MATCHED</p>
        <p className="font-bold">{agency.matched}</p>
      </div>
      <div>
        <p className="text-gray-500">UNMATCHED</p>
        <p className="font-bold text-red-600">{agency.unmatched}</p>
      </div>
      <div>
        <p className="text-gray-500">RESOLVED</p>
        <p className="font-bold text-red-600">{agency.resolved}</p>
      </div>
      <div>
        <p className="text-gray-500">IGNORED</p>
        <p className="font-bold text-red-600">{agency.ignored}</p>
      </div>
    </div>

    <div className="lg:col-span-2">
      <p className="text-xs">{agency.progress}%</p>
      <div className="w-full h-2 bg-gray-200 rounded">
        <div
          className="h-2 bg-blue-600 rounded"
          style={{ width: `${agency.progress}%` }}
        />
      </div>
    </div>

    <div className="lg:col-span-2 text-right">
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(
          agency.status
        )}`}
      >
        {agency.status}
      </span>
    </div>
  </div>
);

/* -------------------- MAIN PAGE -------------------- */

  const AgencyReconciliationPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
  page: 1,
  limit: Number(process.env.REACT_APP_PAGINATION_LIMIT),
  total: 0,
  totalPages: 1,
});

  const [statusFilter, setStatusFilter] = useState("Mismatch");
  const [summaryData,setSummaryData] = useState(null);


  useEffect(() => {
    getAgencyData(pagination.page,pagination.limit);
  }, []);

  useEffect(() => {
    getAgencyData(pagination.page,pagination.limit);
  }, [statusFilter]);

  
  
  async function getAgencyData(page,limit){

     // Fix if accidentally event is passed
  if (typeof page !== "number") {
    page = 1;
    limit = Number(process.env.REACT_APP_PAGINATION_LIMIT);
  }
    apiFetch(
       
          "file-mapping-config/get-invoice-list",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            configId: state.file_mapping_id,
            uploadedFileId: state.id,
            page,
            limit,
            statusFilter:statusFilter
          }),
        }
      ) // <-- replace if needed
      .then((res) => res.json())
      .then((res) => {
        console.log(`res.data   ${res.data.length}`)

        const transformed = transformAgencyData(res.data);
        console.log(`transformed   ${transformed.length}`)
        setAgencies(transformed);
        setPagination(res.pagination || {});

      })
      .catch((err) => {
        console.error("API error:", err);
      })
      .finally(() => setLoading(false));
  }

  /**=========================================
 * PAGINATION BUTTONS
 ==========================================*/
  const pages = getPageNumbers(pagination.page, pagination.totalPages);

  const handleNext = () => {
    if (pagination.page < pagination.totalPages) {
      getAgencyData(pagination.page + 1, pagination.limit);
    }
  };

  const handlePrev = () => {
    if (pagination.page > 1) {
      getAgencyData(pagination.page - 1, pagination.limit);
    }
  };

  const handlePageSizeChange = (e) => {
     //  Reset to first page when page size changes
    const newLimit = Number(e.target.value);
    getAgencyData(1, newLimit);
   
  };

  /**=================================== */

useEffect( () => {
  
    if(state){
      try {
        
        apiFetch("file-mapping-config/summary-details",{
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            configId: state.file_mapping_id,
            uploadedFileId: state.id
          }),
        })
        .then((res) => res.json())
        .then((data) => { 
          let response = data.data;
          setSummaryData(response)
        }
        )
        .catch((err) => console.error(err));
        
      } catch (err) {
        console.error(err);
        alert("Process failed");
      } finally {
        // setRunning(false);
      }
      
    }
    
    
  }, []);

/**================================== */


  return (

    <div className="bg-gray-50 min-h-screen p-6 font-display text-slate-900 min-h-screen flex flex-col">
      {/* MAIN */}
      <main className="flex-1 px-6 md:px-10 py-8 max-w-[1440px] mx-auto w-full">
        {/* Header */}
        <div className="mb-10 flex justify-between items-end">
          <div>
            <h1 className="text-2xl font-bold">Agency Reconciliation</h1>
          <p className="text-gray-500">
              Reviewing mismatch across agencies
            </p>
            <p className="text-gray-500 font-bold mt-1">
              {state.config_name}
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
                        <div className="text-xs font-medium text-slate-700 truncate">
                          Source File : {state.source_file_name}
                        </div>
                        <div className="text-xs font-medium text-slate-700 truncate">
                         Target File : {state.target_file_name}
                        </div>

                    </div>
         
          </div>

          <div className="flex gap-3">
            <button className="bg-gray-200 px-4 py-2 rounded-lg text-sm">
              Export
            </button>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm">
              Run Batch
            </button>
          </div>
        </div>
        

        {/* Summary */}
        <div className="grid md:grid-cols-4 gap-4 mb-10">
          <SummaryCard
            title="Total Agencies"
            value={summaryData?.sourceInvoiceCount}
            subtitle="Grouped Results"
          />
          <SummaryCard
            title="Total Transactions"
            value={summaryData?.sourceUniqueCount}
            subtitle="All Agencies"
          />
          <SummaryCard
            title="Unmatched"
            value={summaryData?.missMatchedCount}
            subtitle="Discrepancies"
          />
          <SummaryCard
            title="Avg Accuracy"
            value={
               summaryData?.accuracy?.toFixed(2) + "%"
            }
            subtitle="Match Rate"
          />
        </div>

        {/* Filter (UI only for now) */}
        <div className="bg-white p-2 rounded-xl mb-6 flex justify-between">
          
          {/* FILTER BUTTONS */}
              <div className="flex items-center gap-3 ml-2">

                <button
                  onClick={() => setStatusFilter("ALL")}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                    statusFilter === "ALL"
                      ? "bg-black text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  All
                </button>

                <button
                  onClick={() => setStatusFilter("Matched")}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                    statusFilter === "Matched"
                      ? "bg-green-600 text-white"
                      : "bg-green-100 text-green-700 hover:bg-green-200"
                  }`}
                >
                  Matched
                </button>

                <button
                  onClick={() => setStatusFilter("Mismatch")}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                    statusFilter === "Mismatch"
                      ? "bg-red-600 text-white"
                      : "bg-red-100 text-red-700 hover:bg-red-200"
                  }`}
                >
                  Mismatched
                </button>

              </div>
          
          <div className="flex gap-3">
            <input
            placeholder="Filter by name or ID..."
            className="border rounded-lg px-3 py-2 w-1/2"
          />
            <select className="border rounded-lg px-3 py-2">
              <option>Status: All</option>
              <option>Completed</option>
              <option>In Progress</option>
              <option>Action Required</option>
            </select>

            {/* <select className="border rounded-lg px-3 py-2">
              <option>Sort</option>
              <option>Transactions</option>
              <option>Match Rate</option>
            </select> */}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-10">Loading...</div>
        ) : (
          <div className="space-y-4">
            <div class="hidden lg:grid grid-cols-12 px-8 py-3 text-on-surface-variant text-xs font-bold uppercase tracking-widest">
                <div class="col-span-2">Agency Name / ID</div>
                <div class="col-span-2 text-center">Total Trans.</div>
                <div class="col-span-4 text-center">Reconciliation Delta</div>
                <div class="col-span-2">Progress</div>
                <div class="col-span-2 text-right">Status</div>
            </div>
            {agencies.map((agency, index) => (
              <AgencyRow
                key={index}
                agency={agency}
                navigate={navigate}
                state={state}
              />
            ))}
          </div>
        )}

        {/* Load More (future pagination) */}
        {/* PAGINATION */}
         <div className="mt-10 text-center">
            <div className="flex justify-between items-center mt-4">

              {/* LEFT */}
              <div className="text-sm text-gray-600">
                Showing{" "}
                {(pagination.page - 1) * pagination.limit + 1}
                {" - "}
                {Math.min(pagination.page * pagination.limit, pagination.total)}
                {" of "}
                {pagination.total}
              </div>

              {/* RIGHT */}
              <div className="flex items-center gap-4">

                {/* Page Size */}
                <div className="flex items-center gap-2">
                  <span className="text-sm">Rows:</span>
                  <select
                    value={pagination.limit}
                    onChange={handlePageSizeChange}
                    className="border px-2 py-1 rounded"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                {/* Prev */}
                <button
                  onClick={handlePrev}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Prev
                </button>

                {/* Page Info */}
                  {pages.map((p, i) =>
                    p === "..." ? (
                      <span key={i} className="px-2">...</span>
                    ) : (
                      <button
                        key={i}
                        onClick={() => getAgencyData(p, pagination.limit)}
                        className={`px-2 py-1 border rounded ${
                          p === pagination.page ? "bg-blue-500 text-white" : ""
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                {/* Next */}
                <button
                  onClick={handleNext}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Next
                </button>

              </div>
            </div>
        </div>
      </main>
    </div>
  );

};

export default AgencyReconciliationPage;



